import '../styles/reports.css';
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  PerformanceChart,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@pagespeedify/ui';
import {
  AlertTriangleIcon,
  ChevronLeftIcon,
  CheckCircle2Icon,
  XCircleIcon,
  CalendarIcon,
  SmartphoneIcon,
  LaptopIcon,
  Share2Icon,
  CopyIcon,
  Loader2Icon
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../lib/apiClient';
import { ActionableInsights } from '../components/reports/ActionableInsights';
import Visualisations from '../components/reports/Visualisations';
import InsightMatrix from '../components/reports/InsightMatrix';
import ReportsTour from '../components/tour/ReportsTour';
import { Report, CreateReportResponseData } from '../types/reports';
// Local type for High Impact insights (matches backend transformer output)
interface HighImpactInsight { id: string; title: string; description: string; weight: number; group: string; normalizedWeight?: number; metricSavings?: { LCP?: number; INP?: number; CLS?: number; FCP?: number; TBT?: number; }; }

// --- Helper: Rating to Class Mapping (Unchanged) ---
const getRatingClasses = (rating: 'good' | 'needs improvement' | 'poor' | 'neutral' | undefined) => {
  switch (rating) {
    case 'good':
      return { text: 'text-report-good', bg: 'bg-report-good-light', fill: 'hsl(var(--report-good))', icon: CheckCircle2Icon };
    case 'needs improvement':
      return { text: 'text-report-improvement', bg: 'bg-report-improvement-light', fill: 'hsl(var(--report-improvement))', icon: AlertTriangleIcon };
    case 'poor':
      return { text: 'text-report-fail', bg: 'bg-report-fail-light', fill: 'hsl(var(--report-fail))', icon: XCircleIcon };
    default:
      return { text: 'text-muted-foreground', bg: 'bg-muted', fill: 'hsl(var(--muted-foreground))', icon: AlertTriangleIcon };
  }
};

const getPerformanceScoreClasses = (score: number) => {
  if (score >= 90) return getRatingClasses('good');
  if (score >= 50) return getRatingClasses('needs improvement');
  return getRatingClasses('poor');
};

// --- Interface Definitions ---
interface PleiData { score: number; name: string; title: string; definition: string; }
interface CrdsData { score: number; name: string; title: string; definition: string; }
interface OeeiData { score: number; name: string; title: string; definition: string; }
interface UaliData { score: number; name: string; title: string; definition: string; }
interface IlriData { score: number; name: string; title: string; definition: string; }
interface RosrData { score: number; name: string; title: string; definition: string; }
interface CoreWebVitalsMetric { value: string; rating: 'good' | 'needs improvement' | 'poor' | 'neutral'; }
interface FilmstripImage { time: string; imageUrl: string; }
interface Opportunity { id: string; title: string; description: string; savings: string; impactedCwvs?: ('LCP' | 'INP' | 'CLS')[]; resolutionSummary?: string; displayValue?: string; affectedResources?: string[]; details?: any; }
interface GroupedOpportunities { LCP: Opportunity[]; INP: Opportunity[]; CLS: Opportunity[]; Other: Opportunity[]; }
interface NotableMetric { id: string; title: string; value: string; rating?: 'good' | 'needs improvement' | 'poor' | 'neutral'; description?: string; }

// This is the shape of the `view` object from the backend.
interface ReportData {
  id: string;
  url: string;
  generatedAt: string;
  performanceScore: number;
  strategy?: 'mobile' | 'desktop';
  domain?: string;
  coreWebVitals: { lcp: CoreWebVitalsMetric; inp: CoreWebVitalsMetric; cls: CoreWebVitalsMetric; };
  filmstrip: FilmstripImage[];
  groupedOpportunities: GroupedOpportunities;
  otherNotableMetrics?: NotableMetric[];
  lighthouseResult?: any;
  highImpactActionableInsights?: HighImpactInsight[];
  plei?: PleiData;
  crds?: CrdsData;
  oeei?: OeeiData;
  uali?: UaliData;
  ilri?: IlriData;
  rosr?: RosrData;
  originalReport?: Report;
}

// This is the full object fetched from the API, containing the pre-processed `view`.
interface R2ReportObject {
  metadata: { reportId: string; strategy: 'mobile' | 'desktop'; requestedAt: string; urlAnalyzed: string; };
  pagespeedData: { lighthouseResult: any; };
  view: ReportData;
  context: any;
}

// --- Helper Functions (Unchanged) ---
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const industryBenchmarks: Record<string, { value: number, label: string, betterIsLower: boolean, unit: string }> = {
  'Largest Contentful Paint (LCP)': { value: 2500, label: '2.5s', betterIsLower: true, unit: 'ms' },
  'Interaction to Next Paint (INP)': { value: 200, label: '200ms', betterIsLower: true, unit: 'ms' },
  'Cumulative Layout Shift (CLS)': { value: 0.1, label: '0.1', betterIsLower: true, unit: '' },
  'First Contentful Paint (FCP)': { value: 1800, label: '1.8s', betterIsLower: true, unit: 'ms' },
  'Total Blocking Time (TBT)': { value: 200, label: '200ms', betterIsLower: true, unit: 'ms' },
  'Speed Index': { value: 3400, label: '3.4s', betterIsLower: true, unit: 'ms' },
  'Time to Interactive (TTI)': { value: 3800, label: '3.8s', betterIsLower: true, unit: 'ms' },
};

const performanceBudgets: Record<string, { budget: number; label: string }> = {
  total: { budget: 1600, label: "Total Page Size" },
  script: { budget: 300, label: "JavaScript" },
  image: { budget: 600, label: "Images" },
  stylesheet: { budget: 100, label: "CSS" },
  font: { budget: 100, label: "Fonts" },
  "third-party": { budget: 400, label: "Third-Party" },
  other: { budget: 50, label: "Other" },
};

// --- React Component ---
const ViewReportsPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const apiClient = useApi();

  const {
    data: r2ReportObject,
    isLoading,
    isError,
    error: queryError,
  } = useQuery<R2ReportObject, Error>({
    queryKey: ['report', reportId],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is undefined');
      return apiClient.get<R2ReportObject>(`/report/${reportId}`);
    },
    enabled: !!reportId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [pieChartDataset, setPieChartDataset] = useState<any | null>(null);
  const [insightFilter, setInsightFilter] = useState<'All' | 'High Impact' | 'LCP' | 'INP' | 'CLS' | 'Other'>('All');
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [publicShareUrl, setPublicShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  const insightGroups = [
    { key: 'All', label: 'All' },
    { key: 'High Impact', label: 'High Impact' },
    { key: 'LCP', label: 'LCP' },
    { key: 'INP', label: 'INP' },
    { key: 'CLS', label: 'CLS' },
    { key: 'Other', label: 'Other' },
  ];

  const scrollToInsight = (metric: string) => {
    const group = metric.includes('LCP') ? 'LCP' : metric.includes('INP') ? 'INP' : metric.includes('CLS') ? 'CLS' : null;
    if (group) {
      const el = document.getElementById(`insights-group-${group}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setInsightFilter(group as any);
    }
  };

  useEffect(() => {
    if (r2ReportObject) {
      setReportData(r2ReportObject.view);
    }
    if (queryError) {
      console.error("[ViewReportsPage] Failed to fetch report:", queryError);
      toast.error("Failed to load report data.", { description: queryError.message });
    }
  }, [r2ReportObject, queryError]);

  useEffect(() => {
    if (reportData?.lighthouseResult?.audits?.['resource-summary']?.details?.items && typeof window !== 'undefined') {
      const resourceSummaryItems = reportData.lighthouseResult.audits['resource-summary'].details.items;
      const getCssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      const chartColorVars = ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6'];
      const computedColors = chartColorVars.map(v => `hsl(${getCssVar(v)})`);
      const borderColor = `hsl(${getCssVar('--background')})`;
      setPieChartDataset({ label: 'Transfer Size (KB)', data: resourceSummaryItems.map((item: any) => parseFloat((item.transferSize / 1024).toFixed(2))), backgroundColor: computedColors, borderColor: borderColor, borderWidth: 2 });
    }
  }, [reportData]);

  const budgetDisplayData = useMemo(() => {
    if (!reportData?.lighthouseResult?.audits?.['resource-summary']?.details?.items) return [];
    const resourceSummaryItems = reportData.lighthouseResult.audits['resource-summary'].details.items;
    return Object.entries(performanceBudgets).map(([key, budgetInfo]) => {
      let size = 0;
      if (key === 'total') {
        size = resourceSummaryItems.reduce((sum: number, item: any) => sum + (item.transferSize || 0), 0) / 1024;
      } else {
        const item = resourceSummaryItems.find((d: any) => d.resourceType === key);
        size = (item?.transferSize || 0) / 1024;
      }
      if (key !== 'total' && size === 0) return null;
      const percentage = (size / budgetInfo.budget) * 100;
      const isOverBudget = size > budgetInfo.budget;
      let indicatorClassName: string, textColorClassName: string;
      if (isOverBudget) { indicatorClassName = "[&>div]:bg-report-fail"; textColorClassName = "text-report-fail"; }
      else if (percentage > 80) { indicatorClassName = "[&>div]:bg-report-improvement"; textColorClassName = "text-report-improvement"; }
      else { indicatorClassName = "[&>div]:bg-report-good"; textColorClassName = "text-report-good"; }
      return { key, label: budgetInfo.label, size, budget: budgetInfo.budget, percentage: Math.min(percentage, 100), isOverBudget, indicatorClassName, textColorClassName };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  }, [reportData]);

  const performanceMetricsToDisplay = useMemo(() => {
    if (!reportData) return [];
    const metrics: Array<{ title: string; value: string; rating: CoreWebVitalsMetric['rating']; description?: string }> = [];
    if (reportData.coreWebVitals) {
      metrics.push({ title: 'Largest Contentful Paint (LCP)', ...reportData.coreWebVitals.lcp, description: "Measures loading performance. To provide a good user experience, LCP should occur within 2.5 seconds of when the page first starts loading." });
      metrics.push({ title: 'Interaction to Next Paint (INP)', ...reportData.coreWebVitals.inp, description: "Measures overall responsiveness to user interactions. An INP below or at 200 milliseconds means your page has good responsiveness." });
      metrics.push({ title: 'Cumulative Layout Shift (CLS)', ...reportData.coreWebVitals.cls, description: "Measures visual stability. To provide a good user experience, pages should maintain a CLS of 0.1. or less." });
    }
    const notableMetricsMap: { [key: string]: { title: string, description: string } } = {
      'first-contentful-paint': { title: 'First Contentful Paint (FCP)', description: "Marks the time at which the first text or image is painted. A good FCP is 1.8 seconds or less." },
      'total-blocking-time': { title: 'Total Blocking Time (TBT)', description: "Measures the total amount of time that a page is blocked from responding to user input. A good TBT is 200 milliseconds or less." },
      'speed-index': { title: 'Speed Index', description: "Shows how quickly the contents of a page are visibly populated. A good Speed Index is 3.4 seconds or less." },
      'time-to-interactive': { title: 'Time to Interactive (TTI)', description: "Measures the time from when the page starts loading to when its main sub-resources have loaded and it is capable of reliably responding to user input quickly. A good TTI is 3.8 seconds or less." }
    };
    reportData.otherNotableMetrics?.forEach(metric => {
      if (notableMetricsMap[metric.id]) {
        metrics.push({ title: notableMetricsMap[metric.id].title, value: metric.value, rating: metric.rating || 'neutral', description: notableMetricsMap[metric.id].description });
      }
    });
    return metrics;
  }, [reportData]);

  if (isLoading || (reportId && !reportData && !isError)) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-1/4 mb-4" />
        <Skeleton className="h-4 w-1/2 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
        <Skeleton className="h-12 w-full mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !reportData) {
    return (
      <div className="p-6 text-center">
        <AlertTriangleIcon className="mx-auto h-12 w-12 text-report-fail" />
        <h2 className="mt-4 text-xl font-semibold">Failed to load report</h2>
        <p className="text-gray-600">{queryError?.message || "The report data could not be processed or found."}</p>
        <Button onClick={() => navigate(-1)} className="mt-6"><ChevronLeftIcon className="mr-2 h-4 w-4" /> Go Back</Button>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto p-4 md:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-tour="report-header">
          <div>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="mb-2 sm:mb-0"><ChevronLeftIcon className="mr-2 h-4 w-4" /> Back to Reports</Button>
            <h1 className="text-3xl font-bold text-foreground truncate" title={reportData.domain}>Report for: {reportData.domain}</h1>
            <p className="text-xs text-muted-foreground/70 mb-1" title={reportData.url}>Tested: {reportData.url}</p>
            <p className="text-sm text-muted-foreground">Analyzed on: {formatDate(reportData.generatedAt)} (Device: <span className="capitalize">{reportData.strategy}</span>)</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" disabled={isRegenerating} onClick={async () => {
              if (!reportId) {
                toast.error("Report ID is missing.");
                return;
              }

              setIsRegenerating(true);
              try {
                // First, get the existing report to extract URL and device
                const existingReport = await apiClient.get(`/reports/${reportId}`) as any;
                const originalUrl = existingReport.metadata?.urlAnalyzed || existingReport.pagespeedData?.lighthouseResult?.finalUrl;
                const originalStrategy = existingReport.metadata?.strategy || 'mobile';

                if (!originalUrl) {
                  toast.error("Could not extract URL from existing report");
                  return;
                }

                // Now create a new report with the same URL and strategy
                const response = await apiClient.post<CreateReportResponseData>('/reports', {
                  website_url: originalUrl,
                  page_url: originalUrl,
                  strategy: originalStrategy
                });

                if (response) {
                  const reportId = response.reportId || response.report?.id;
                  if (reportId) {
                    // Navigate to reports page with the generating report ID in state
                    navigate('/reports', { state: { generatingReportId: reportId } });
                  } else {
                    toast.error("Could not get new report ID");
                  }
                }
              } catch (error: any) {
                console.error('Error regenerating report:', error);
                toast.error("Failed to regenerate report", {
                  description: error.message || "Please try again later"
                });
              } finally {
                setIsRegenerating(false);
              }
            }}>{isRegenerating ? 'Regenerating...' : 'Regenerate'}</Button>
            <Button variant="outline" size="sm" onClick={async () => {
              if (!reportId) { toast.error("Report ID is missing."); return; }
              setIsSharing(true); setPublicShareUrl(null);
              try {
                const response = await apiClient.post<{ publicUrl: string }>(`/reports/${reportId}/share`, {});
                if (response.publicUrl) {
                  setPublicShareUrl(response.publicUrl);
                  navigator.clipboard.writeText(response.publicUrl);
                  toast.success("Shareable link copied!", { description: response.publicUrl, action: { label: "Open", onClick: () => window.open(response.publicUrl, '_blank') } });
                } else { toast.error("Failed to generate shareable link."); }
              } catch (err: any) {
                const errorMessage = err.response?.data?.error || err.message || "An unknown error occurred.";
                toast.error("Failed to create shareable link.", { description: errorMessage });
              } finally { setIsSharing(false); }
            }} disabled={isSharing}>
              {isSharing ? <Loader2Icon className="mr-2 h-4 w-4 animate-spin" /> : publicShareUrl ? <CopyIcon className="mr-2 h-4 w-4" /> : <Share2Icon className="mr-2 h-4 w-4" />}
              {isSharing ? "Generating..." : publicShareUrl ? "Copy Link" : "Share"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>Print</Button>
          </div>
        </div>

        <Card id="performance-summary" className="mb-8 border-0 shadow-none" data-tour="performance-score">
          <CardHeader className="border-b-0"><CardTitle className="text-2xl font-semibold text-card-foreground">Performance Summary</CardTitle></CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="relative w-[120px] h-[120px]">
                  <svg className={`w-full h-full ${getPerformanceScoreClasses(reportData.performanceScore).text}`} viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="54" fill="none" stroke="hsl(var(--muted))" strokeWidth="12" />
                    <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="12" strokeDasharray="339.292" strokeDashoffset={339.292 - (reportData.performanceScore / 100) * 339.292} transform="rotate(-90 60 60)" strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s cubic-bezier(.4,2,.3,1)' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><span className={`text-3xl font-bold ${getPerformanceScoreClasses(reportData.performanceScore).text}`}>{reportData.performanceScore}</span></div>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1"><XCircleIcon className="w-4 h-4 text-report-fail" /><span className="text-xs text-report-fail">Poor (0-49)</span></div>
                  <div className="flex items-center gap-1"><AlertTriangleIcon className="w-4 h-4 text-report-improvement" /><span className="text-xs text-report-improvement">Needs Improvement (50-89)</span></div>
                  <div className="flex items-center gap-1"><CheckCircle2Icon className="w-4 h-4 text-report-good" /><span className="text-xs text-report-good">Good (90-100)</span></div>
                </div>
                {reportData.lighthouseResult && (
                  <div className="mt-4 p-3 rounded-lg bg-muted/30 flex flex-wrap gap-x-4 gap-y-2 items-center text-xs">
                    <div className="flex items-center gap-2" title="Date of Analysis"><CalendarIcon className="w-4 h-4 text-muted-foreground" /><span className="font-medium text-muted-foreground">{formatDate(reportData.generatedAt)}</span></div>
                    <div className="flex items-center gap-2" title="Device Strategy">{reportData.strategy === 'mobile' ? <SmartphoneIcon className="w-4 h-4 text-muted-foreground" /> : <LaptopIcon className="w-4 h-4 text-muted-foreground" />}<span className="capitalize font-medium text-muted-foreground">{reportData.strategy}</span></div>
                    <div className="flex items-center gap-2" title="Lighthouse Version"><span className="font-medium text-muted-foreground">Lighthouse:</span><span className="font-mono text-muted-foreground">{reportData.lighthouseResult.lighthouseVersion}</span></div>
                  </div>
                )}
              </div>
              <div data-tour="core-web-vitals">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {performanceMetricsToDisplay.map((metric) => {
                    const { text: color, icon: Icon } = getRatingClasses(metric.rating);
                    const bench = industryBenchmarks[metric.title];
                    let numeric = parseFloat((metric.value || '').replace(/[^\d.]/g, ''));
                    if (metric.title === 'CLS') numeric = parseFloat(metric.value);
                    if (metric.value && metric.value.includes('s') && !metric.value.includes('ms')) { numeric = numeric * 1000; }
                    let comparison = '', comparisonColor = '';
                    if (bench && !isNaN(numeric)) {
                      const better = bench.betterIsLower ? numeric <= bench.value : numeric >= bench.value;
                      const isTimingMetric = bench.unit === 'ms' || bench.unit === 's';
                      if (better) { comparison = `${isTimingMetric ? 'Faster' : 'Better'} than industry avg: ${bench.label}`; comparisonColor = 'bg-report-good-light text-report-good'; }
                      else { comparison = `${isTimingMetric ? 'Slower' : 'Worse'} than industry avg: ${bench.label}`; comparisonColor = 'bg-report-fail-light text-report-fail'; }
                    }
                    return (
                      <Tooltip key={metric.title} delayDuration={100}>
                        <TooltipTrigger asChild>
                          <div className={`flex flex-col gap-1 p-4 rounded-lg bg-card cursor-pointer`} onClick={() => scrollToInsight(metric.title)} title={`Jump to actionable insights for ${metric.title}`}>
                            <div className="flex items-center gap-2 mb-1"><Icon className={`w-5 h-5 ${color}`} /><span className="text-sm font-medium text-card-foreground">{metric.title}</span></div>
                            <div className="flex items-center gap-2"><span className={`text-2xl font-bold ${color}`}>{metric.value}</span></div>
                            {comparison && (<span className={`mt-1 text-xs font-semibold rounded px-2 py-1 ${comparisonColor}`}>{comparison}</span>)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-sm"><p>{metric.description || "No description available."}</p></TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {reportData && (
          <Card id="performance-metrics-overview" className="mb-8 border-0 shadow-none" data-tour="metrics-overview">
            <CardHeader className="border-b-0"><CardTitle className="text-2xl font-semibold text-card-foreground">Performance Metrics Overview</CardTitle></CardHeader>
            <CardContent className="p-6">
              <Tabs defaultValue="failed-metrics" className="w-full">
                <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="failed-metrics">Metrics Needing Attention</TabsTrigger><TabsTrigger value="passed-metrics">Metrics Performing Well</TabsTrigger></TabsList>
                <TabsContent value="failed-metrics" className="mt-6">
                  <InsightMatrix
                    pleiData={reportData.plei && reportData.plei.score <= 70 ? reportData.plei : undefined}
                    crdsData={reportData.crds && reportData.crds.score >= 30 ? reportData.crds : undefined}
                    oeeiData={reportData.oeei && reportData.oeei.score <= 70 ? reportData.oeei : undefined}
                    ualiData={reportData.uali && reportData.uali.score >= 30 ? reportData.uali : undefined}
                    ilriData={reportData.ilri && reportData.ilri.score >= 30 ? reportData.ilri : undefined}
                    rosrData={reportData.rosr && reportData.rosr.score >= 30 ? reportData.rosr : undefined}
                  />
                </TabsContent>
                <TabsContent value="passed-metrics" className="mt-6">
                  <InsightMatrix
                    pleiData={reportData.plei && reportData.plei.score > 70 ? reportData.plei : undefined}
                    crdsData={reportData.crds && reportData.crds.score < 30 ? reportData.crds : undefined}
                    oeeiData={reportData.oeei && reportData.oeei.score > 70 ? reportData.oeei : undefined}
                    ualiData={reportData.uali && reportData.uali.score < 30 ? reportData.uali : undefined}
                    ilriData={reportData.ilri && reportData.ilri.score < 30 ? reportData.ilri : undefined}
                    rosrData={reportData.rosr && reportData.rosr.score < 30 ? reportData.rosr : undefined}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        <div data-tour="actionable-insights">
          <ActionableInsights
            reportObject={r2ReportObject || null}
            insightFilter={insightFilter}
            setInsightFilter={setInsightFilter}
            insightGroups={insightGroups}
            openItems={Array.from(openItems)}
            setOpenItems={(items: string[]) => setOpenItems(new Set(items))}
            highImpactInsights={reportData.highImpactActionableInsights || []}
          />
        </div>

        <div data-tour="visualizations">
          <Visualisations
            filmstrip={reportData.filmstrip || []}
            budgetDisplayData={budgetDisplayData}
            resourceSummaryItems={reportData.lighthouseResult?.audits?.['resource-summary']?.details?.items || []}
            pieChartDataset={pieChartDataset}
            PerformanceChartComponent={PerformanceChart}
          />
        </div>
        <ReportsTour />
      </div>
    </TooltipProvider>
  );
};

export default ViewReportsPage;
